import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { X } from "lucide-react";
import { RECORD_LINK_RELATION, TASK_KIND, TASK_STATUS } from "@brainlog/shared";
import { del, errorMessage, get, post } from "@/lib/api";
import { openNote, openPaper, openView } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import type { RecordDetail } from "./RecordPanel";
import { LibraryPicker, NotePicker } from "./pickers";
import { KindIcon } from "@/features/notes/kinds";
import type { Task } from "@/lib/types";

const DEFAULT_RELATION: Record<string, string> = {
  study_plan: "addresses",
  study_execution: "run_of",
  result: "result_of",
  decision: "informs",
};

export function RecordLinks(props: { noteId: string; kind: string; workspaceId: string; detail: RecordDetail; readOnly: boolean; onChange(): void }) {
  const [open, setOpen] = useState(false);
  const relLabel = (r: string) => (RECORD_LINK_RELATION.labels as Record<string, string>)[r] ?? r;
  const openTarget = (l: RecordDetail["links"][number]) => {
    const t = l.target;
    if (!t.accessible) return;
    if (l.toType === "note") openNote(l.toId, t.title ?? "Record");
    else if (l.toType === "paper") openPaper(l.toId, t.title ?? "Paper");
    else if (l.toType === "task") openView("backlog", props.workspaceId, "Backlog", { task: l.toId });
    else if (l.toType === "manuscript") openView("manuscript", props.workspaceId, t.title ?? "Manuscript", undefined, l.toId);
    else if (l.toType === "manuscript_section" && t.noteId) openNote(t.noteId, t.title ?? "Section");
  };
  return (
    <div className="space-y-2 text-sm">
      <ul className="space-y-1">
        {props.detail.links.map((l) => (
          <li key={l.id} className="flex items-center gap-2 rounded border bg-background px-2 py-1">
            <span className="shrink-0 text-xs text-muted-foreground">{relLabel(l.relation)}</span>
            {l.target.accessible ? (
              <button type="button" className="min-w-0 flex-1 truncate text-left underline" onClick={() => openTarget(l)}>
                {l.target.title}
              </button>
            ) : (
              <span className="flex-1 text-muted-foreground">Unavailable item</span>
            )}
            {!props.readOnly && (
              <button
                type="button"
                aria-label="Remove link"
                className="rounded p-0.5 hover:bg-accent"
                onClick={async () => {
                  await del(`/api/record-links/${l.id}`);
                  props.onChange();
                }}
              >
                <X className="size-3.5" />
              </button>
            )}
          </li>
        ))}
        {props.detail.incomingLinks.map((l) => (
          <li key={l.id} className="flex items-center gap-2 rounded border border-dashed px-2 py-1">
            <KindIcon kind={l.from.kind} />
            <button type="button" className="min-w-0 flex-1 truncate text-left underline" onClick={() => openNote(l.from.id, l.from.title)}>
              {l.from.title}
            </button>
            <span className="shrink-0 text-xs text-muted-foreground">links here ({relLabel(l.relation)})</span>
          </li>
        ))}
        {!props.detail.links.length && !props.detail.incomingLinks.length && <li className="text-muted-foreground">No linked records.</li>}
      </ul>
      {!props.readOnly && (
        <Button size="xs" variant="outline" onClick={() => setOpen(true)}>
          Link a record, paper, task or section…
        </Button>
      )}
      {open && <AddLinkDialog {...props} defaultRelation={DEFAULT_RELATION[props.kind] ?? "related"} onClose={() => setOpen(false)} />}
    </div>
  );
}

function AddLinkDialog(props: { noteId: string; workspaceId: string; defaultRelation: string; onChange(): void; onClose(): void }) {
  const [type, setType] = useState<"note" | "paper" | "task" | "manuscript_section">("note");
  const [relation, setRelation] = useState(props.defaultRelation);
  const [target, setTarget] = useState<{ id: string; title: string } | null>(null);
  const tasks = useQuery({ queryKey: ["tasks", props.workspaceId], queryFn: () => get<{ tasks: Task[] }>(`/api/tasks?workspaceId=${props.workspaceId}`), enabled: type === "task" });
  const mss = useQuery({
    queryKey: ["manuscripts-sections", props.workspaceId],
    enabled: type === "manuscript_section",
    queryFn: async () => {
      const { manuscripts } = await get<{ manuscripts: { id: string; title: string }[] }>(`/api/workspaces/${props.workspaceId}/manuscripts`);
      const out: { id: string; title: string }[] = [];
      for (const m of manuscripts) {
        const d = await get<{ sections: { id: string; title: string }[] }>(`/api/manuscripts/${m.id}`);
        for (const s of d.sections) out.push({ id: s.id, title: `${m.title} › ${s.title}` });
      }
      return out;
    },
  });
  const save = async () => {
    if (!target) return;
    try {
      await post("/api/record-links", { fromNoteId: props.noteId, toType: type, toId: target.id, relation });
      props.onChange();
      props.onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && props.onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a link</DialogTitle>
          <DialogDescription>Links keep working when you rename or move things. You can link only to items in this notebook or project.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="rl-type">Link to</Label>
            <select id="rl-type" className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={type} onChange={(e) => { setType(e.target.value as any); setTarget(null); }}>
              <option value="note">Note or research record</option>
              <option value="paper">Library item / paper</option>
              <option value="task">Task</option>
              <option value="manuscript_section">Manuscript section</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="rl-rel">Relation</Label>
            <select id="rl-rel" className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={relation} onChange={(e) => setRelation(e.target.value)}>
              {RECORD_LINK_RELATION.values.map((r) => (
                <option key={r} value={r}>
                  {RECORD_LINK_RELATION.labels[r]}
                </option>
              ))}
            </select>
          </div>
        </div>
        {type === "note" && <NotePicker workspaceId={props.workspaceId} excludeIds={[props.noteId]} selected={target ? [target.id] : []} onPick={(id, title) => setTarget({ id, title })} />}
        {type === "paper" && <LibraryPicker workspaceId={props.workspaceId} selectedId={target?.id} onPick={(i) => setTarget({ id: i.id, title: i.title })} />}
        {type === "task" && (
          <ul className="max-h-48 overflow-y-auto rounded-md border p-1 text-sm">
            {tasks.data?.tasks.map((t) => (
              <li key={t.id}>
                <button type="button" className={`w-full rounded px-2 py-1 text-left hover:bg-accent ${target?.id === t.id ? "bg-accent font-medium" : ""}`} onClick={() => setTarget({ id: t.id, title: t.title })}>
                  {t.title} <span className="text-xs text-muted-foreground">({(TASK_KIND.labels as Record<string, string>)[t.kind] ?? t.kind}, {((TASK_STATUS.labels as Record<string, string>)[t.status] ?? t.status).toLowerCase()})</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {type === "manuscript_section" && (
          <ul className="max-h-48 overflow-y-auto rounded-md border p-1 text-sm">
            {mss.data?.map((s) => (
              <li key={s.id}>
                <button type="button" className={`w-full rounded px-2 py-1 text-left hover:bg-accent ${target?.id === s.id ? "bg-accent font-medium" : ""}`} onClick={() => setTarget(s)}>
                  {s.title}
                </button>
              </li>
            ))}
            {mss.data && !mss.data.length && <li className="px-2 py-1 text-muted-foreground">No manuscript sections.</li>}
          </ul>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!target}>
            Add link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
